import upperCase from "lodash/upperCase"
import {
  FILESTACK_API_KEY,
  FILESTACK_OPTIONS,
  MEDIA_EXTENSIONS_MAP,
  MEDIA_TYPES,
  type MediaType
} from "./constants"
import { PickerOptions } from "./types"

/**
 * Video Encoding Options
 * Configuration for video processing
 */
export const videoEncodingOptions = {
  video_convert: {
    preset: "hls.variant", // This is also Filestack's default
    clip_length: "20:00:00",
    aspect_mode: "preserve"
  }
}

/**
 * Get storage configuration for Filestack uploads
 */
export const getStoreTo = (
  mediaStoragePath = ""
): PickerOptions["storeTo"] => ({
  container: "pw-filestack-uploads",
  path: mediaStoragePath,
  region: "us-east-1"
})

/**
 * Get Filestack picker options for a media type
 */
export const getOptions = (
  mediaType: keyof typeof MEDIA_TYPES | MediaType | string,
  overrides: Partial<PickerOptions> = {}
): PickerOptions => {
  // mediaType is optional.
  const normalizedType = (
    mediaType || MEDIA_TYPES.ANY
  ).toUpperCase() as MediaType

  const options =
    // Force mediaType to be uppercase.
    FILESTACK_OPTIONS[normalizedType] ||
    // Default to MEDIA_TYPES.ANY options.
    FILESTACK_OPTIONS[MEDIA_TYPES.ANY]

  return {
    ...options,
    ...overrides
  }
}

/**
 * Get uploader options with storage path
 * Used by media manager components
 */
export const getUploaderOptions = ({
  mediaType,
  pickOptions,
  mediaStoragePath
}: {
  mediaType: MediaType
  pickOptions: PickerOptions
  mediaStoragePath: string
}): PickerOptions => {
  // store the media in the appropriate account bucket
  const storeTo = getStoreTo(mediaStoragePath)

  const options = getOptions(upperCase(mediaType) as MediaType, {
    ...pickOptions,
    storeTo
  })

  return options
}

/**
 * Extract Filestack handle from URL
 */
export const getFilestackHandle = (
  filestackUrl: string
): string | undefined => {
  const filestackHandleRe = new RegExp(
    "https:\\/\\/cdn\\.filestackcontent\\.com\\/(?<handle>[a-zA-Z0-9]+)"
  )

  if (filestackUrl) {
    try {
      return filestackUrl.match(filestackHandleRe)?.groups?.handle
    } catch (error) {
      console.error(`Error getting filestack handle: ${error}`)
    }
  }
}

/**
 * Check whether a MIME type is an HEIC or HEIF image.
 */
export const isHeic = (mimetype?: string | null): boolean =>
  /^image\/hei[cf](?:;|$)/i.test(mimetype || "")

/**
 * Get preview URL from handle
 */
export const getPreviewURL = (handle: string): string =>
  `https://cdn.filestackcontent.com/preview/${handle}`

/**
 * Create download URL for Filestack file
 */
export const createFilestackDownloadURL = (handle = "", name = ""): string => {
  const base = "https://cdn.filestackcontent.com"
  const content = `content=t:attachment,f:%22${encodeURIComponent(
    removeHTML(name)
  )}%22`

  return `${base}/${FILESTACK_API_KEY}/${content}/${handle}`
}

/**
 * Check if URL is a Filestack/filepicker URL
 */
export const isFilePickerURL = (url: string): boolean => {
  return (
    (url && url.indexOf("filepicker.io") > -1) || url.indexOf("filestack") > -1
  )
}

/**
 * Format file size in human-readable format
 */
export const getHumanFileSize = (size: number): string => {
  const i = Math.floor(Math.log(size) / Math.log(1024))
  // Not sure how properly to convert the string to number here.
  const value = ((size / Math.pow(1024, i)).toFixed(2) as unknown as number) * 1
  const unit = ["B", "kB", "MB", "GB", "TB"][i]
  return `${value} ${unit}`
}

/**
 * Format a list with Oxford comma style
 * e.g., ["a", "b", "c"] with "or" -> "a, b, or c"
 */
const oxfordCommatize = (list: string[], conjunction: string): string => {
  if (list.length === 0) return ""
  if (list.length === 1) return list[0]

  const listCopy = [...list]
  listCopy[listCopy.length - 1] = `${conjunction} ${
    listCopy[listCopy.length - 1]
  }`
  return listCopy.join(", ")
}

/**
 * Format extension list for human-readable display
 */
export const getHumanExtensionList = (mediaType: MediaType): string => {
  const extensions = [...(MEDIA_EXTENSIONS_MAP[mediaType] || [])]
  if (!extensions.length) return ""
  if (extensions.length > 1) {
    return `a ${oxfordCommatize(extensions, "or")} file`
  }

  return `${extensions[0]} file`
}

/**
 * Get signed Filestack URL
 * Currently returns URL as-is (legacy function, logic commented out)
 */
export const getSignedFilestackURL = (
  url: string,
  extraParams?: any,
  convert?: any
): string => {
  // Legacy function - implementation was commented out
  // Keeping for backward compatibility
  return url
}

/**
 * Remove HTML tags from string
 * Simple utility for sanitizing filenames
 */
const removeHTML = (html: string): string => {
  if (typeof window === "undefined") return html
  // Create a new div element
  const tempDivElement = window.document.createElement("div")
  // Set the HTML content with the provided
  tempDivElement.innerHTML = html
  // Retrieve the text property of the element (cross-browser support)
  return tempDivElement.textContent || tempDivElement.innerText || ""
}
